import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, CheckCircle2, Eye, Wallet, ArrowUpRight, Store, MousePointerClick, Loader2, AlertCircle, MessageSquare } from 'lucide-react';
import styles from './WhatsAppDashboard.module.css';
import { useStore } from '../../../../context/StoreContext';
import { SMIIFY_API } from '../../../../config/api';

// WhatsApp overview: totals from campaigns, the line in use and the wallet.
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const WhatsAppDashboard = () => {
  const navigate = useNavigate();
  const { activeStore } = useStore();
  const isHQ = !activeStore || activeStore?.isHQ === true || (activeStore?._id || activeStore?.id) === 'global_hq';
  const storeId = isHQ ? 'global_hq' : String(activeStore?._id || activeStore?.id);
  const [campaigns, setCampaigns] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  const ready = Boolean(activeStore);
  useEffect(() => {
    if (!ready) return;
    const h = { Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, 'x-store-id': storeId };
    setLoading(true);
    Promise.all([
      fetch(`${SMIIFY_API}/campaigns`, { headers: h }).then((r) => r.json()).catch(() => ({})),
      fetch(`${SMIIFY_API}/wallet/balance`, { headers: h }).then((r) => r.json()).catch(() => ({})),
    ]).then(([c, w]) => { setCampaigns(c.success ? c.data || [] : []); setWallet(w.success ? w.data : null); }).finally(() => setLoading(false));
  }, [ready, storeId]);

  const s = useMemo(() => {
    let sent = 0; let delivered = 0; let read = 0; let clicked = 0; const tpl = {};
    campaigns.forEach((c) => {
      const m = c.metrics || {};
      sent += m.sent || 0; delivered += m.delivered || 0; read += m.read || 0; clicked += m.clicked || 0;
      const k = c.templateName || c.template;
      if (k) { tpl[k] = tpl[k] || { name: k, sent: 0, read: 0, clicks: 0 }; tpl[k].sent += m.sent || 0; tpl[k].read += m.read || 0; tpl[k].clicks += m.clicked || 0; }
    });
    return { sent, delivered, read, readRate: delivered ? ((read / delivered) * 100).toFixed(1) : 0, clickRate: read ? ((clicked / read) * 100).toFixed(1) : 0, top: Object.values(tpl).sort((a, b) => b.sent - a.sent).slice(0, 3) };
  }, [campaigns]);

  if (!activeStore) return <div className={styles.dashboardContainer} style={{ justifyContent: 'center', alignItems: 'center' }}><h3 style={{ color: '#64748b', fontSize: 14 }}>Select a branch or Global HQ</h3></div>;

  const stats = [
    { title: 'Sent', value: s.sent.toLocaleString('en-IN'), icon: Send, bg: '#f5f6ff', color: '#673de6', note: 'All campaigns' },
    { title: 'Delivered', value: s.delivered.toLocaleString('en-IN'), icon: CheckCircle2, bg: '#dcfce7', color: '#10b981', note: 'Reached phones' },
    { title: 'Read rate', value: `${s.readRate}%`, icon: Eye, bg: '#fef3c7', color: '#d97706', note: 'Of delivered' },
    { title: 'Click rate', value: `${s.clickRate}%`, icon: MousePointerClick, bg: '#e6e8ff', color: '#7b66ff', note: 'Of read' },
    { title: 'Wallet', value: wallet ? inr(wallet.available) : '—', icon: Wallet, bg: wallet && wallet.available < 100 ? '#fee2e2' : '#f1f9f3', color: wallet && wallet.available < 100 ? '#ef4444' : '#007a49', note: wallet?.held ? `${inr(wallet.held)} reserved` : 'Available', link: '/wallet-billing' },
  ];

  return (
    <div className={styles.dashboardContainer} style={{ opacity: loading ? 0.6 : 1, transition: 'opacity .3s' }}>
      <header className={styles.dashboardHeader}>
        <div className={styles.headerLeft}>
          <h1>WhatsApp overview</h1>
          <div className={styles.storeBadge}><Store size={12} strokeWidth={2.5} /> {isHQ ? 'Global HQ' : activeStore?.storeName || activeStore?.name}</div>
          {wallet?.line && <div className={styles.storeBadge}><MessageSquare size={12} /> {wallet.line.label}</div>}
        </div>
        <div className={styles.headerRight}>
          <button type="button" className={styles.primaryBtn} onClick={() => navigate('/marketing/campaigns/create')}><Send size={12} /> New campaign</button>
        </div>
      </header>

      {!wallet?.line && !loading && (
        <div style={{ margin: '0 0 14px', padding: '10px 14px', borderRadius: 10, background: '#fff4f3', color: '#be1025', fontWeight: 700, fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
          <AlertCircle size={15} /> No WhatsApp line is ready. <button type="button" onClick={() => navigate('/integrations/whatsapp')} style={{ border: 'none', background: 'none', color: '#673de6', fontWeight: 900, cursor: 'pointer' }}>Set it up</button>
        </div>
      )}

      <div className={styles.statsGrid}>
        {stats.map((x) => (
          <div key={x.title} className={styles.statCard} onClick={x.link ? () => navigate(x.link) : undefined} style={x.link ? { cursor: 'pointer' } : undefined}>
            <div className={styles.statHeader}><span className={styles.statTitle}>{x.title.toUpperCase()}</span><div className={styles.iconWrapper} style={{ backgroundColor: x.bg }}><x.icon size={16} color={x.color} strokeWidth={2.5} /></div></div>
            <div className={styles.statValueArea}><h2>{x.value}</h2><span className={`${styles.trendBadge} ${styles.trendNeutral}`}>{x.note}</span></div>
          </div>
        ))}
      </div>

      <div className={styles.dashboardSplit}>
        <div className={styles.cardSection}>
          <div className={styles.cardHeader}><h3>Recent campaigns</h3><button type="button" className={styles.linkBtn} onClick={() => navigate('/marketing/campaigns')}>View all <ArrowUpRight size={12} strokeWidth={2.5} /></button></div>
          <div className={styles.tableWrapper}>
            <table className={styles.customTable}>
              <thead><tr><th>Campaign</th><th>Sent</th><th>Read</th><th>Cost</th><th>Status</th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan="5" style={{ textAlign: 'center', padding: '40px 0' }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#673de6' }} /></td></tr>
                  : campaigns.length === 0 ? <tr><td colSpan="5" style={{ textAlign: 'center', padding: '30px 0', color: '#64748b', fontSize: 13 }}>No campaigns yet.</td></tr>
                    : campaigns.slice(0, 5).map((c) => {
                      const m = c.metrics || {};
                      return (
                        <tr key={c._id} onClick={() => navigate(`/marketing/campaigns/${c._id}`)} style={{ cursor: 'pointer' }}>
                          <td><div className={styles.fw600}>{c.name}</div><div className={styles.textMuted}>{c.date} · {c.templateName || c.template}</div></td>
                          <td className={styles.fw600}>{(m.sent || 0).toLocaleString('en-IN')}</td>
                          <td>{m.delivered ? Math.round(((m.read || 0) / m.delivered) * 100) : 0}%</td>
                          <td>{inr((c.cost?.charged || 0) - (c.cost?.refunded || 0))}</td>
                          <td><span className={`${styles.statusBadge} ${c.status === 'Completed' ? styles.badgeCompleted : styles.badgeActive}`}>{c.status === 'Processing' ? 'Sending' : c.status === 'Cancelled' ? 'Stopped' : c.status}</span></td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
          </div>
        </div>
        <div className={styles.cardSection}>
          <div className={styles.cardHeader}><h3>Top templates</h3></div>
          <div className={styles.templateList}>
            {s.top.length ? s.top.map((t) => (
              <div key={t.name} className={styles.templateItem}>
                <div className={styles.templateHeader}><h4>{t.name}</h4><span className={styles.catBadge}>TEMPLATE</span></div>
                <div className={styles.templateStats}>
                  <div className={styles.tStat}><span>Sent</span><span>{t.sent >= 1000 ? `${(t.sent / 1000).toFixed(1)}K` : t.sent}</span></div>
                  <div className={styles.tStat}><span>Read</span><span>{t.sent ? Math.round((t.read / t.sent) * 100) : 0}%</span></div>
                  <div className={styles.tStat}><span>Clicks</span><span>{t.read ? Math.round((t.clicks / t.read) * 100) : 0}%</span></div>
                </div>
              </div>
            )) : <div style={{ textAlign: 'center', padding: '30px 0', color: '#64748b', fontSize: 13 }}>No template data yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppDashboard;
